/**
 * Simple Wishlist logic for Product Cards
 * Stores objects: { handle, variantId, sku, variantTitle }
 * Backwards-compatible: legacy string entries are treated as handle-only.
 */
class Wishlist {
    constructor() {
        this.storageKey = 'astra-wishlist';
        this.migrateIfNeeded();
        this.init();
    }

    init() {
        document.addEventListener('click', (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const button = target.closest('[data-wishlist-button]');
            if (button instanceof HTMLElement) {
                event.preventDefault();
                event.stopPropagation();
                this.toggle(button);
            }
        });

        document.addEventListener('wishlist:updated', () => this.updateHeaderCount());

        this.updateAllButtons();
        const observer = new MutationObserver((mutations) => {
            let shouldUpdate = false;
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    for (const node of mutation.addedNodes) {
                        if (node instanceof Element) {
                            if (node.querySelector('[data-wishlist-button]') || node.hasAttribute('data-wishlist-button')) {
                                shouldUpdate = true;
                                break;
                            }
                        }
                    }
                }
                if (shouldUpdate) break;
            }
            if (shouldUpdate) this.updateAllButtons();
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    /** Migrate old string[] format to object[] format */
    migrateIfNeeded() {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        try {
            const data = JSON.parse(raw);
            if (!Array.isArray(data)) return;
            let needsMigration = false;
            const migrated = data.map(item => {
                if (typeof item === 'string') {
                    needsMigration = true;
                    return { handle: item, variantId: null, sku: '', variantTitle: '' };
                }
                return item;
            });
            if (needsMigration) {
                localStorage.setItem(this.storageKey, JSON.stringify(migrated));
            }
        } catch (e) {
            // corrupted data, reset
            localStorage.setItem(this.storageKey, '[]');
        }
    }

    /**
     * @param {HTMLElement} button
     */
    toggle(button) {
        const handle = button.getAttribute('data-product-handle');
        if (!handle) return;

        let wishlist = this.getWishlist();
        const index = wishlist.findIndex(item => item.handle === handle);

        if (index > -1) {
            wishlist.splice(index, 1);
            button.removeAttribute('data-wishlist-active');
        } else {
            const variantId = button.getAttribute('data-variant-id') || null;
            const sku = button.getAttribute('data-variant-sku') || '';
            const variantTitle = button.getAttribute('data-variant-title') || '';
            wishlist.push({ handle, variantId, sku, variantTitle });
            button.setAttribute('data-wishlist-active', 'true');
        }

        this.saveWishlist(wishlist);
        this.updateAllButtons();

        document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist } }));
    }

    getWishlist() {
        const data = localStorage.getItem(this.storageKey);
        if (!data) return [];
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    /** Returns an array of handles for quick lookup */
    getHandles() {
        return this.getWishlist().map(item => typeof item === 'string' ? item : item.handle);
    }

    /**
     * @param {Array} wishlist
     */
    saveWishlist(wishlist) {
        localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
    }

    updateAllButtons() {
        const handles = this.getHandles();
        document.querySelectorAll('[data-wishlist-button]').forEach(button => {
            const handle = button.getAttribute('data-product-handle');
            if (handles.includes(handle)) {
                button.setAttribute('data-wishlist-active', 'true');
            } else {
                button.removeAttribute('data-wishlist-active');
            }
        });
        this.updateHeaderCount();
    }

    updateHeaderCount() {
        const wishlist = this.getWishlist();
        const count = wishlist.length;
        const wrappers = document.querySelectorAll('[data-wishlist-count-wrapper]');

        wrappers.forEach(wrapper => {
            const bubble = wrapper.querySelector('[data-wishlist-count-bubble]');
            const countElement = wrapper.querySelector('[data-wishlist-count]');

            if (count > 0) {
                if (bubble && bubble.classList.contains('visually-hidden')) {
                    bubble.classList.remove('visually-hidden');
                }
                if (countElement && countElement.textContent !== count.toString()) {
                    countElement.textContent = count;
                }
                if (!wrapper.classList.contains('header-actions__wishlist-icon--has-items')) {
                    wrapper.classList.add('header-actions__wishlist-icon--has-items');
                }
            } else {
                if (bubble && !bubble.classList.contains('visually-hidden')) {
                    bubble.classList.add('visually-hidden');
                }
                if (wrapper.classList.contains('header-actions__wishlist-icon--has-items')) {
                    wrapper.classList.remove('header-actions__wishlist-icon--has-items');
                }
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
  /** @type {any} */ (window).astraWishlist = new Wishlist();
});
